import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useGetLogPermissions, useSetAllLogPermissions } from "@/api/log-permissions.api";
import { LogRole } from "@/constants/log-templates.constants";
import { ParametersHeader } from "@/components/shared/parametersHeader";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import Paths from "@/constants/paths";
import { LogPermissionsTable } from "@/components/backOffice/parameters/logPermissionsTable";
import { UpdateLogPermissionsDto, LogPermissionEntryDto } from "@/types/api/api-types";

export const LogPermissionsParameterPage = () => {
  const { t } = useTranslation("boParameters");
  const { data: permissions, isLoading } = useGetLogPermissions();
  const { mutate: updatePermissions } = useSetAllLogPermissions();

  const [isBoMode, setIsBoMode] = useState(true);

  const navigate = useNavigate();

  const handleToggle = (
    action: LogPermissionEntryDto["action"],
    role: LogRole,
    checked: boolean,
  ) => {
    const existingPermission = permissions?.find(
      (p) => p.action === action && p.role === role && p.isBo === isBoMode,
    );
    const payload: UpdateLogPermissionsDto = {
      permissions: [
        {
          id: existingPermission?.id,
          action,
          role,
          isBo: isBoMode,
          canRead: checked,
        },
      ],
    };
    updatePermissions(payload);
  };

  return (
    <ParametersHeader>
      <div className="flex w-full flex-1 flex-col gap-6 lg:px-8">
        <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
          <div className="flex w-full flex-col justify-between gap-2 md:flex-row">
            <div className="items-left flex flex-col gap-2">
              <h2 className="text-lg font-semibold">{t("logPermissionsPage.title")}</h2>
              <p className="text-sm text-gray-600">{t("logPermissionsPage.description")}</p>
            </div>
            <Button variant="outline" onClick={() => navigate(Paths.bo.parametersUsers)}>
              <ArrowLeft className="h-4 w-4" />
              {t("logPermissionsPage.backToUsers")}
            </Button>
          </div>
        </div>

        <div className="flex w-fit items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
          <Button
            variant={isBoMode ? "default" : "ghost"}
            size="sm"
            onClick={() => setIsBoMode(true)}
          >
            {t("logPermissionsPage.boMode")}
          </Button>
          <Button
            variant={!isBoMode ? "default" : "ghost"}
            size="sm"
            onClick={() => setIsBoMode(false)}
            className={`hover:bg-green-200 ${!isBoMode ? "bg-green-500 hover:bg-green-500/80" : ""}`}
          >
            {t("logPermissionsPage.clientsMode")}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <LogPermissionsTable
            isBoMode={isBoMode}
            permissions={permissions ?? []}
            onToggle={handleToggle}
          />
        )}
      </div>
    </ParametersHeader>
  );
};
